import { Component, OnDestroy, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { IFormsProvider, LFOfflineOrder, SAVE_FORM_RESULT, UserInformation } from '@lfw/roesberg.liveforms.core';
import { LiveformsEditorComponent } from '@lfw/roesberg.liveforms.editor';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, firstValueFrom, map, take, tap } from 'rxjs';
import { LfwRouterService } from '../../../route/lfw-router.service';
import { AppToolbarButtonComponent } from '../../../shared/components/app-toolbar-button/app-toolbar-button.component';
import { AppToolbarDividerComponent } from '../../../shared/components/app-toolbar-divider/app-toolbar-divider.component';
import { AppToolbarTextComponent } from '../../../shared/components/app-toolbar-text/app-toolbar-text.component';
import { DefaultDialogService } from '../../../shared/services/default-dialog.service';
import { SettingsService } from '../../../shared/settings/settings.service';
import { MenuService } from '../../../shell/menu.service';
import { UserInformationService } from '../../../shell/user-information.service';
import { FormEditToolbarComponent } from '../../components/form-toolbar/form-edit-toolbar.component';
import { OrderEditorMobileMenuComponent } from '../../components/order-editor-mobile-menu/order-editor-mobile-menu.component';
import { SubStateSelectComponent } from '../../components/sub-state-select/sub-state-select.component';
import { OfflineFormsProvider } from '../offline-forms.provider';

@Component({
  selector: 'lfw-offline-order-editor',
  templateUrl: './offline-order-editor.component.html',
  styleUrls: ['./offline-order-editor.component.css'],
  providers: [{ provide: IFormsProvider, useClass: OfflineFormsProvider }],
  imports: [
    LiveformsEditorComponent,
    FormEditToolbarComponent,
    AppToolbarButtonComponent,
    AppToolbarDividerComponent,
    AppToolbarTextComponent,
    SubStateSelectComponent,
    TranslatePipe,
    OrderEditorMobileMenuComponent
  ]
})
export class OfflineOrderEditorComponent implements OnDestroy {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly offlineOrderClient = inject(IFormsProvider<LFOfflineOrder>);
  private readonly routerService = inject(LfwRouterService);
  private readonly userInfoService = inject(UserInformationService);
  private readonly menuService = inject(MenuService);
  private readonly dialogService = inject(DefaultDialogService);
  private readonly title = inject(Title);
  private readonly settingsService = inject(SettingsService);

  order: LFOfflineOrder | undefined;
  user: UserInformation = this.userInfoService.userInformation;
  changedForm = signal(false);
  editorSettings = this.settingsService.getEditorSettings();
  editor = viewChild.required(LiveformsEditorComponent);

  constructor() {
    this.activatedRoute.paramMap
      .pipe(
        takeUntilDestroyed(),
        map((paramMap) => paramMap.get('id')),
        filter((orderId): orderId is string => !!orderId),
        map(async (orderId) => {
          return await firstValueFrom<LFOfflineOrder>(this.offlineOrderClient.loadForm(orderId)).catch(() => undefined);
        }),
        map(async (order) => {
          const resolved = await order;
          if (!resolved) {
            this.dialogService.formNotFound(this.activatedRoute.snapshot.params['id']);
          } else {
            this.order = resolved;
            this.title.setTitle(this.order.form.order.text);
          }
        }),
        take(1)
      )
      .subscribe();
    this.menuService.hideNavigation();
  }

  get caption(): string {
    return this.order?.form?.order?.text ?? '';
  }

  ngOnDestroy(): void {
    this.menuService.showNavigation();
  }

  canSave(): boolean {
    return this.changedForm();
  }

  close(): void {
    this.routerService.navigateToOfflineOrdersOverview();
  }

  save(): void {
    this.editor()
      .save()
      .pipe(
        tap((result) => {
          if (result === SAVE_FORM_RESULT.FAILED) {
            this.dialogService.showSaveError(null); //TODO: pass error details, e.g. disk full, quota exceeded, etc.?
          } else {
            this.editor().formGroup.markAsPristine();
            this.changedForm.set(false);
          }
        }),
        take(1)
      )
      .subscribe();
  }

  finalize(): void {
    //TODO: implement save and close
    if (!this.order || !this.editor) {
      return;
    }
  }

  formChanged(): void {
    this.changedForm.set(this.editor().formGroup.dirty);
  }

  subStateSelectionChanged(): void {
    this.editor().formGroup.markAsDirty();
  }
}
